import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  MessageSquare,
  Trash2,
  Pencil,
  Check,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  LogOut,
  Search,
  NotebookPen,
  Wand2,
  FolderOpen,
  Download,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { sessionsApi, exportSession } from "../lib/api";
import { GlitchLogo, AssistantMark } from "./GlitchLogo";

export const Sidebar = ({
  open,
  onToggle,
  sessions,
  activeSessionId,
  onSelectSession,
  onNewSession,
  onRenameSession,
  onDeleteSession,
  onOpenSettings,
  onOpenNotes,
  onOpenStudio,
  onOpenFiles,
  onLogout,
  userName,
  assistantName = "Glitch",
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditText(s.title);
  };
  const commitEdit = async () => {
    if (!editText.trim()) return setEditingId(null);
    try {
      await onRenameSession(editingId, editText.trim());
      toast.success("Renamed");
    } catch (e) {
      toast.error("Rename failed");
    }
    setEditingId(null);
  };

  const doSearch = async (val) => {
    setQuery(val);
    if (!val.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const res = await sessionsApi.search(val.trim());
      setSearchResults(res);
    } catch (e) {
      /* ignore */
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setSearchResults(null);
  };

  const listToShow = searchResults ? searchResults.sessions : sessions;
  const msgHits = searchResults?.messages || [];

  // Highlight helper
  const highlight = (text) => {
    if (!query.trim()) return text;
    const parts = text.split(new RegExp(`(${escapeRe(query)})`, "ig"));
    return parts.map((p, i) =>
      p.toLowerCase() === query.toLowerCase() ? (
        <mark
          key={i}
          className="bg-blue-500/40 text-white rounded px-0.5"
        >
          {p}
        </mark>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      )
    );
  };

  return (
    <>
      {/* Toggle handle */}
      <button
        data-testid="sidebar-toggle-btn"
        onClick={onToggle}
        className="fixed top-5 left-5 z-40 h-10 w-10 rounded-full glass hover:bg-white/10 flex items-center justify-center transition-colors"
        aria-label="Toggle sidebar"
      >
        {open ? (
          <PanelLeftClose className="h-5 w-5 text-blue-300" />
        ) : (
          <PanelLeftOpen className="h-5 w-5 text-blue-300" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.aside
            data-testid="sidebar"
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
            className="fixed inset-y-0 left-0 z-30 w-80 glass-strong flex flex-col pt-20 pb-4 px-4"
          >
            <div className="flex items-center gap-2.5 mb-4 pl-1">
              <AssistantMark name={assistantName} size={36} />
              <div className="min-w-0">
                <div className="font-display text-base font-semibold truncate">
                  {assistantName}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-blue-300/60 truncate">
                  Hey, {userName}
                </div>
              </div>
            </div>

            <Button
              data-testid="new-chat-btn"
              onClick={onNewSession}
              className="mb-3 bg-blue-600 hover:bg-blue-500 text-white rounded-full font-medium h-11 shadow-md shadow-blue-600/30"
            >
              <Plus className="h-4 w-4 mr-2" /> New conversation
            </Button>

            <Button
              data-testid="open-notes-btn"
              onClick={onOpenNotes}
              variant="ghost"
              className="mb-2 rounded-full h-10 justify-start text-white/80 hover:text-white hover:bg-white/5 border border-white/10"
            >
              <NotebookPen className="h-4 w-4 mr-2 text-blue-300" />
              Voice notes
            </Button>

            <Button
              data-testid="open-studio-btn"
              onClick={onOpenStudio}
              variant="ghost"
              className="mb-3 rounded-full h-10 justify-start text-white/80 hover:text-white hover:bg-white/5 border border-white/10"
            >
              <Wand2 className="h-4 w-4 mr-2 text-blue-300" />
              Studio · Image · Video
            </Button>

            <Button
              data-testid="open-files-btn"
              onClick={onOpenFiles}
              variant="ghost"
              className="mb-3 rounded-full h-10 justify-start text-white/80 hover:text-white hover:bg-white/5 border border-white/10"
            >
              <FolderOpen className="h-4 w-4 mr-2 text-blue-300" />
              Files
            </Button>

            {/* Search */}
            <div className="relative mb-2">
              <Search className="h-3.5 w-3.5 text-white/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                data-testid="search-input"
                value={query}
                onChange={(e) => doSearch(e.target.value)}
                placeholder="Search history…"
                className="h-9 pl-8 pr-8 bg-black/30 border-white/10 focus-visible:ring-blue-500/40 text-sm"
              />
              {query && (
                <button
                  data-testid="clear-search-btn"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center"
                  aria-label="Clear search"
                >
                  <X className="h-3 w-3 text-white/60" />
                </button>
              )}
            </div>

            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2 pl-1 flex items-center justify-between">
              <span>{searchResults ? "Results" : "History"}</span>
              {searching && (
                <span className="text-white/30">Searching…</span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto -mr-2 pr-2 space-y-1">
              {listToShow.length === 0 && (
                <div className="text-sm text-white/40 px-2 py-4">
                  {searchResults
                    ? "No matches."
                    : "No conversations yet. Tap the mic to start."}
                </div>
              )}
              {listToShow.map((s) => {
                const active = s.id === activeSessionId;
                const isEditing = editingId === s.id;
                const hitsForSession = msgHits.filter(
                  (m) => m.session_id === s.id
                );
                return (
                  <div
                    key={s.id}
                    data-testid={`session-item-${s.id}`}
                    className={`group relative rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                      active
                        ? "bg-blue-500/15 border border-blue-400/30"
                        : "hover:bg-white/5 border border-transparent"
                    }`}
                    onClick={() => !isEditing && onSelectSession(s.id)}
                  >
                    <div className="flex items-center gap-2">
                      <MessageSquare
                        className={`h-3.5 w-3.5 shrink-0 ${
                          active ? "text-blue-300" : "text-white/50"
                        }`}
                      />
                      {isEditing ? (
                        <div
                          className="flex items-center gap-1 flex-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Input
                            data-testid="rename-input"
                            autoFocus
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitEdit();
                              if (e.key === "Escape") setEditingId(null);
                            }}
                            className="h-7 text-sm bg-black/40 border-white/10"
                          />
                          <button
                            onClick={commitEdit}
                            className="h-7 w-7 rounded-md hover:bg-white/10 flex items-center justify-center"
                          >
                            <Check className="h-3.5 w-3.5 text-emerald-400" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="h-7 w-7 rounded-md hover:bg-white/10 flex items-center justify-center"
                          >
                            <X className="h-3.5 w-3.5 text-white/60" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="text-sm truncate flex-1 text-white/90">
                            {highlight(s.title)}
                          </div>
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5">
                            <button
                              data-testid={`export-btn-${s.id}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await exportSession(s.id);
                                  toast.success("Exported to Markdown");
                                } catch (err) {
                                  toast.error("Export failed");
                                }
                              }}
                              className="h-6 w-6 rounded hover:bg-white/10 flex items-center justify-center"
                              aria-label="Export"
                              title="Export to Markdown"
                            >
                              <Download className="h-3 w-3 text-white/60" />
                            </button>
                            <button
                              data-testid={`rename-btn-${s.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                startEdit(s);
                              }}
                              className="h-6 w-6 rounded hover:bg-white/10 flex items-center justify-center"
                              aria-label="Rename"
                            >
                              <Pencil className="h-3 w-3 text-white/60" />
                            </button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button
                                  data-testid={`delete-btn-${s.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-6 w-6 rounded hover:bg-red-500/20 flex items-center justify-center"
                                  aria-label="Delete"
                                >
                                  <Trash2 className="h-3 w-3 text-red-400/80" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="glass-strong border-white/10">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="font-display">
                                    Delete this conversation?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    &ldquo;{s.title}&rdquo; will be gone for good.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid="delete-cancel-btn">
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    data-testid={`confirm-delete-${s.id}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDeleteSession(s.id);
                                    }}
                                    className="bg-red-600 hover:bg-red-500"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Matched message snippets */}
                    {hitsForSession.length > 0 && (
                      <div className="mt-1.5 pl-5 space-y-1">
                        {hitsForSession.slice(0, 3).map((m) => (
                          <div
                            key={m.id}
                            className="text-[11px] text-white/50 leading-snug truncate"
                          >
                            <span
                              className={
                                m.role === "user"
                                  ? "text-blue-300/70"
                                  : "text-white/40"
                              }
                            >
                              {m.role === "user" ? "you: " : `${assistantName}: `}
                            </span>
                            {highlight(snippetAround(m.text, query, 60))}
                          </div>
                        ))}
                        {hitsForSession.length > 3 && (
                          <div className="text-[10px] text-white/30">
                            +{hitsForSession.length - 3} more matches
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="pt-3 mt-3 border-t border-white/10 flex gap-2">
              <Button
                data-testid="settings-btn"
                variant="ghost"
                onClick={onOpenSettings}
                className="flex-1 text-white/70 hover:text-white hover:bg-white/5 justify-start"
              >
                <Settings className="h-4 w-4 mr-2" /> Settings
              </Button>
              <Button
                data-testid="logout-btn"
                variant="ghost"
                onClick={onLogout}
                className="text-white/70 hover:text-red-300 hover:bg-red-500/10"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
};

function escapeRe(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function snippetAround(text, query, len) {
  if (!query) return text.slice(0, len);
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return text.slice(0, len);
  const start = Math.max(0, idx - Math.floor(len / 3));
  const end = Math.min(text.length, start + len);
  return (start > 0 ? "…" : "") + text.slice(start, end) + (end < text.length ? "…" : "");
}
